import type { FC } from 'react';
import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardHeader,
  Skeleton,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Iconify,
  Label,
  TableFillerTextStyled,
} from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab';
import { YEAR_MONTH_SHORTDAY_FORMAT } from '@pflegenavi/shared/constants';
import { useFormatting, useLocale } from '@pflegenavi/frontend/localization';
import { range } from 'lodash';
import NoDataImage from '../../assets/undraw_no_records.svg';
import {
  PaymentInitiatedBy,
  TransactionInitiatedBy,
  TransactionSourceType,
} from '@pflegenavi/shared/api';
import type { PendingCharge as ApiPendingCharge } from '@pflegenavi/frontend/api-nursing-home';
import { PendingChargeStatus } from '@pflegenavi/shared/api';
import {
  getPaymentViewData,
  getPendingChargeViewData,
  type PaymentViewData,
} from '@pflegenavi/shared/frontend';

// Define the subset of PendingCharge properties needed for this view
export type PaymentViewPendingCharge = Pick<
  ApiPendingCharge,
  | 'amount'
  | 'charge_date'
  | 'payment_initiation'
  | 'pending_charge_status'
  | 'family_member_first_name'
  | 'family_member_last_name'
>;

// Helper to check if a charge has failed
function isFailedCharge(charge: PaymentViewPendingCharge): boolean {
  return (
    charge.pending_charge_status === PendingChargeStatus.failed ||
    charge.pending_charge_status === PendingChargeStatus.frozen
  );
}

// ----------------------------------------------------------------------

interface PaymentRowViewData {
  id: string;
  amount: number;
  date: Date;
  pending: boolean;
  typeText: string;
  statusText?: string;
  color: 'warning' | 'error' | 'info' | 'success';
  initiatedBy?: PaymentInitiatedBy | TransactionInitiatedBy;
  pendingChargeStatus?: string;
  creatorName?: string;
  familyMemberName?: string;
}

// Interface for the minimum data needed to display a payment
interface PaymentData {
  id: string;
  amountInCent: number;
  date: Date;
  sourceType: TransactionSourceType;
  initiatedBy: TransactionInitiatedBy;
  disputed?: boolean;
  cancelled?: boolean;
  creatorName?: string;
}

export interface LatestFMPaymentViewProps {
  isLoading: boolean;
  data: PaymentData[];
  allPendingCharges?: PaymentViewPendingCharge[];
  nursingHomePhone?: string;
  nursingHomeName?: string;
  onViewAll?: () => void;
  maxRows?: number;
}

export const LatestFMPaymentView: FC<LatestFMPaymentViewProps> = ({
  isLoading,
  data,
  allPendingCharges = [] as PaymentViewPendingCharge[],
  nursingHomePhone,
  nursingHomeName,
  onViewAll,
  maxRows = 5,
}) => {
  const { t } = useTranslation();

  // Check if there are any failed charges that need nursing home contact guidance
  const hasFailedCharges = useMemo(() => {
    return allPendingCharges.some(isFailedCharge);
  }, [allPendingCharges]);

  const payments = useMemo(() => {
    const transformPayment = (
      payment: PaymentData & {
        pending_charge?: PaymentViewPendingCharge;
        nursingHomeName: string;
      },
      isPending = false
    ): PaymentRowViewData => {
      let viewData: PaymentViewData;
      if (payment.pending_charge) {
        viewData = getPendingChargeViewData(
          payment.pending_charge,
          payment.nursingHomeName,
          t
        );
      } else {
        viewData = getPaymentViewData(payment, payment.nursingHomeName, t);
      }

      return {
        id: payment.id,
        amount: payment.amountInCent,
        date: payment.date,
        pending: isPending,
        typeText: viewData.typeText,
        statusText: viewData.statusText,
        color: viewData.color,
        initiatedBy: payment.initiatedBy,
        creatorName: payment.creatorName,
        familyMemberName: viewData.initiatorName,
      };
    };

    let allPayments: PaymentRowViewData[] = data
      .map((payment) =>
        transformPayment({
          ...payment,
          nursingHomeName: nursingHomeName ?? '--',
        })
      )
      .sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        if (dateA === dateB) {
          return 0;
        }
        return dateB > dateA ? 1 : -1;
      });

    // Add all pending charges from the allPendingCharges prop
    if (allPendingCharges.length > 0) {
      const pendingViewData = allPendingCharges.map((charge, index) => {
        const pendingPayment: PaymentData & {
          pending_charge: PaymentViewPendingCharge;
          nursingHomeName: string;
        } = {
          id: `pending-charge-${index}`,
          amountInCent: charge.amount,
          date: new Date(charge.charge_date),
          sourceType: TransactionSourceType.DirectDebit,
          disputed: false,
          initiatedBy:
            charge.payment_initiation === PaymentInitiatedBy.FamilyMember
              ? TransactionInitiatedBy.FamilyMember
              : charge.payment_initiation === PaymentInitiatedBy.Employee
              ? TransactionInitiatedBy.Employee
              : TransactionInitiatedBy.System,
          cancelled: false,
          pending_charge: charge,
          nursingHomeName: nursingHomeName ?? '--',
        };
        return transformPayment(pendingPayment, true);
      });

      allPayments = [...pendingViewData, ...allPayments];
    }
    return allPayments;
  }, [data, allPendingCharges, t, nursingHomeName]);

  const showViewAll = data?.length > 0;

  return (
    <PaymentsContainer raised>
      <CardHeader
        title={t('mobile.family-member.latest-payments')}
        sx={{ mb: 2 }}
        titleTypographyProps={{
          sx: { fontWeight: 600 },
        }}
      />

      {hasFailedCharges && nursingHomePhone && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            mb: 2,
            mx: 2,
            backgroundColor: 'error.lighter',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'error.light',
          }}
        >
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Iconify
              icon="bxs:error"
              color="error.main"
              fontSize={20}
              sx={{ mr: 1 }}
            />
            <Typography
              variant="subtitle2"
              color="error.main"
              sx={{ fontWeight: 600 }}
            >
              {t('mobile.family-member.failed-payment')}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t('mobile.family-member.failed-payment-contact-nursing-home', {
              phone: nursingHomePhone?.replace(/\s/g, '\xa0'),
            })}
          </Typography>
        </Box>
      )}

      <Timeline>
        {isLoading &&
          range(maxRows).map((row: number, index) => (
            <PaymentRowSkeleton
              key={`row-${row}`}
              isLast={index === maxRows - 1}
            />
          ))}

        {!isLoading && payments.length === 0 && (
          <Stack
            justifyContent="center"
            alignItems="center"
            sx={{
              p: 3,
            }}
          >
            <img
              alt="empty content"
              src={NoDataImage}
              style={{ marginBottom: 25 }}
              height={160}
              width={200}
            />
            <TableFillerTextStyled>{t('table.no-data')}</TableFillerTextStyled>
          </Stack>
        )}

        {!isLoading &&
          payments
            .slice(0, maxRows)
            .map((payment, index) => (
              <PaymentRow
                key={payment.id}
                payment={payment}
                isLast={index === Math.min(payments.length - 1, maxRows - 1)}
                nursingHomeName={nursingHomeName}
              />
            ))}
      </Timeline>

      {showViewAll && onViewAll && (
        <Box sx={{ p: 1, textAlign: 'right', mt: 'auto' }}>
          <Button
            size="small"
            color="inherit"
            endIcon={<Iconify icon={'eva:arrow-ios-forward-fill'} />}
            onClick={onViewAll}
          >
            {t('dashboard.label.viewAll')}
          </Button>
        </Box>
      )}
    </PaymentsContainer>
  );
};

interface PaymentRowProps {
  payment: PaymentRowViewData;
  isLast: boolean;
  nursingHomeName?: string;
}

const PaymentRow: FC<PaymentRowProps> = ({
  payment,
  isLast,
  nursingHomeName,
}) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const { fCurrencyCents } = useFormatting();

  const { date, amount, typeText, statusText, color, initiatedBy } = payment;

  const getInitiatorIcon = (
    initiatedBy?: PaymentInitiatedBy | TransactionInitiatedBy
  ) => {
    switch (initiatedBy) {
      case PaymentInitiatedBy.FamilyMember:
      case TransactionInitiatedBy.FamilyMember:
        return (
          <Tooltip title={t('web-components.initiator.family-member')} arrow>
            <Iconify icon="eva:people-fill" fontSize={16} sx={{ mr: 0.5 }} />
          </Tooltip>
        );
      case PaymentInitiatedBy.Employee:
      case TransactionInitiatedBy.Employee:
        return (
          <Tooltip title={t('web-components.initiator.employee')} arrow>
            <Iconify icon="eva:person-fill" fontSize={16} sx={{ mr: 0.5 }} />
          </Tooltip>
        );
      case PaymentInitiatedBy.System:
      case TransactionInitiatedBy.System:
        return (
          <Tooltip title={t('web-components.initiator.system')} arrow>
            <Iconify
              icon="eva:settings-2-fill"
              fontSize={16}
              sx={{ mr: 0.5 }}
            />
          </Tooltip>
        );
      default:
        return null;
    }
  };

  return (
    <TimelineItem>
      <TimelineSeparator>
        <TimelineDot color={color} />
        {isLast ? null : <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent sx={{ pr: 0 }}>
        <Stack direction="row" justifyContent="space-between">
          <Stack>
            <Stack direction="row" alignItems="center" gap={0.5}>
              {getInitiatorIcon(initiatedBy)}
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {typeText}
              </Typography>
              {statusText && (
                <Typography variant="body1" sx={{ color: 'grey.500' }}>
                  ({statusText})
                </Typography>
              )}
            </Stack>

            <Typography
              variant="subtitle2"
              color="grey.500"
              sx={{ fontWeight: 500, fontSize: 14, lineHeight: 1.1 }}
            >
              {new Date(date).toLocaleDateString(
                locale,
                YEAR_MONTH_SHORTDAY_FORMAT
              )}
            </Typography>
            {initiatedBy && (
              <Typography
                variant="caption"
                color="grey.500"
                sx={{ fontSize: 12, lineHeight: 1.2, mt: 0.5 }}
              >
                {t('payments.initiated-by-label', {
                  name:
                    initiatedBy === TransactionInitiatedBy.FamilyMember
                      ? payment.familyMemberName || payment.creatorName
                      : nursingHomeName,
                })}
              </Typography>
            )}
          </Stack>
          <Label variant="ghost" color={color} sx={{ mt: 0.4 }}>
            {fCurrencyCents(amount)}
          </Label>
        </Stack>
      </TimelineContent>
    </TimelineItem>
  );
};

const PaymentRowSkeleton: FC<{ isLast: boolean }> = ({ isLast }) => {
  return (
    <TimelineItem>
      <TimelineSeparator>
        <TimelineDot color="grey" />
        {isLast ? null : <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Stack>
            <Typography variant="body1">
              <Skeleton width={200} />
            </Typography>

            <Typography
              variant="subtitle2"
              color="grey.500"
              sx={{ lineHeight: 1.1 }}
            >
              <Skeleton />
            </Typography>
          </Stack>
          <Skeleton width={40} />
        </Stack>
      </TimelineContent>
    </TimelineItem>
  );
};

const PaymentsContainer = styled(Card)(({ theme }) =>
  theme.unstable_sx({
    minHeight: '490px',
    '& .MuiTimelineItem-missingOppositeContent:before': {
      display: 'none',
    },
  })
);
